import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, Input, QueryList, ViewChildren } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FormControlEx } from '../../forms/form-control';
import { InputControlBase } from '../../forms/input-control.base';
import { RadioGroupItem } from '../../radio-group/radio-group-item';
import { AstralFieldShellComponent } from '../astral-field-shell/astral-field-shell.component';
import { shouldShowAstralValidationError } from '../astral-validation-state';
import { AstralFieldAccessibilityDirective, getAstralFieldLabelId } from '../astral-field-accessibility.util';

@Component({
  selector: 'astral-radio-group',
  standalone: true,
  imports: [TranslatePipe, AstralFieldShellComponent, AstralFieldAccessibilityDirective],
  template: `
    <astral-field-shell
      [control]="resolvedControlEx"
      [label]="resolvedLabel"
      [helpText]="helpText"
      [invalid]="resolvedInvalid"
      [errorMessage]="resolvedErrorMessage"
      [forId]="resolvedInputId"
      [associateLabel]="false"
      [hideLabel]="!resolvedLabel"
    >
      <div
        astralFieldAccessibility
        class="astral-radio-group"
        role="radiogroup"
        [attr.id]="resolvedInputId"
        [attr.aria-labelledby]="labelId"
        [astralFieldControl]="control"
        [astralFieldHelpText]="helpText"
        [astralFieldInputId]="resolvedInputId"
        [astralFieldInvalid]="resolvedInvalid"
        [class.astral-radio-group--disabled]="resolvedDisabled"
      >
        @for (item of items; track item.value; let index = $index) {
          <label
          class="astral-radio-group__option"
          [class.astral-radio-group__option--checked]="isChecked(item.value)"
          [class.astral-radio-group__option--disabled]="resolvedDisabled"
        >
          <input
            #input
            class="astral-radio-group__native"
            type="radio"
            [attr.name]="resolvedInputName"
            [checked]="isChecked(item.value)"
            [disabled]="resolvedDisabled ? true : null"
            (blur)="handleBlur()"
            (change)="handleChange(item.value)"
            (focus)="handleFocus(index)"
          />
          <span class="astral-radio-group__control" aria-hidden="true"></span>
          <span class="astral-radio-group__text">{{ item.displayName | translate }}</span>
          </label>
        }
      </div>
    </astral-field-shell>
  `,
  styleUrls: ['./astral-radio-group.component.scss'],
})
export class AstralRadioGroupComponent<TValue = string | boolean> extends InputControlBase<TValue> implements AfterViewInit {
  private static nextId = 0;
  private readonly uid = `astral-radio-group-${++AstralRadioGroupComponent.nextId}`;

  constructor(private readonly cd: ChangeDetectorRef) {
    super();
  }

  @Input() items: RadioGroupItem<TValue>[] = [];
  @Input() caption: string;
  @Input() disabled = false;
  @Input() focused = false;
  @Input() inputName: string;
  @Input() span: number | 'full' = 1;

  @ViewChildren('input') inputs!: QueryList<ElementRef<HTMLInputElement>>;

  isFocused = false;
  private focusedIndex = 0;

  @HostBinding('tabindex') tabindex = -1;

  @HostBinding('style.grid-column')
  get hostGridColumn(): string | null {
    if (this.span === 'full') {
      return '1 / -1';
    }

    if (!this.span || this.span <= 1) {
      return null;
    }

    return `span ${this.span}`;
  }

  get resolvedControlEx(): FormControlEx<TValue> | null {
    return this.control as FormControlEx<TValue>;
  }

  get resolvedDisabled(): boolean {
    return this.control?.disabled ?? this.disabled;
  }

  get resolvedErrorMessage(): { message: string; params: any } | null {
    if (shouldShowAstralValidationError(this.control) && this.control?.errors) {
      return (this.control as any).getErrorMessage?.() ?? { message: 'ERROR.REQUIRED', params: {} };
    }

    return null;
  }

  get resolvedInputName(): string {
    if (this.inputName) {
      return this.inputName;
    }

    const source = this.resolvedLabel;
    return (
      source
        ?.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || this.uid
    );
  }

  get resolvedInputId(): string {
    return this.uid;
  }

  get labelId(): string {
    return getAstralFieldLabelId(this.resolvedInputId);
  }

  get resolvedInvalid(): boolean {
    return shouldShowAstralValidationError(this.control);
  }

  get resolvedLabel(): string | null {
    return this.caption ?? this.resolvedControlEx?.caption ?? null;
  }

  @HostListener('focus')
  focusHost(): void {
    const input = this.inputs?.get(this.focusedIndex)?.nativeElement ?? this.inputs?.first?.nativeElement;
    input?.focus();
  }

  ngAfterViewInit(): void {
    if (this.focused) {
      this.inputs?.first?.nativeElement.focus();
      this.isFocused = true;
      this.cd.detectChanges();
    }
  }

  handleBlur(): void {
    this.isFocused = false;
    this.control?.markAsTouched();
    this.onblur();
  }

  handleChange(value: TValue): void {
    const previousValue = this.control?.value;
    this.control?.setValue(value);
    this.control?.markAsDirty();
    this.valueChange.emit({
      formSnapshot: this.control?.parent ? Object.assign({}, this.control.parent.value) : null,
      newValue: value,
      previousValue,
    });
  }

  handleFocus(index: number): void {
    this.focusedIndex = index;
    this.isFocused = true;
    this.onfocus();
  }

  isChecked(value: TValue): boolean {
    return this.control?.value === value;
  }
}
